/**
 * Pure reducer functions for the narrative combat panel (#260 / 07 §13.9.5).
 *
 * No side effects — all functions return new state; no DOM, no llui imports.
 * The turn log is ephemeral client Model state (JSON-serialisable Records —
 * no Maps/Sets/Dates). Cleared with the initiative state on session reset.
 *
 * Design note (07 §13.9.5):
 * > The panel is not a combat engine. Rolls go through the standard dice
 * > surface; the GM still decides "did the attack hit?" The panel records the
 * > outcomes the GM narrates; it does not adjudicate them.
 */

// ---------------------------------------------------------------------------
// Turn log types
// ---------------------------------------------------------------------------

/** What kind of action a turn-log entry records. */
export type TurnLogEntryKind =
  | 'effect'
  | 'damage'
  | 'condition'
  | 'reaction_roll'
  | 'save';

/** One entry in the per-round, per-actor action log. */
export interface TurnLogEntry {
  /** Stable synthetic id (monotonic within the session). */
  id: string;
  /** Which round this action occurred in. */
  round: number;
  /** Initiative-entry id of the acting combatant. */
  actorEntryId: string;
  /** Display name of the acting combatant at the time of the action. */
  actorName: string;
  /** Category of action. */
  kind: TurnLogEntryKind;
  /** Human-readable summary — also the text that becomes a draft narration. */
  description: string;
}

/** The full ephemeral turn log for one encounter. */
export interface TurnLog {
  entries: TurnLogEntry[];
}

/** An empty turn log (use as the initial state). */
export const EMPTY_TURN_LOG: TurnLog = { entries: [] };

// ---------------------------------------------------------------------------
// NPC quick-pick actions (affordance 4)
// ---------------------------------------------------------------------------

/** Valid NPC quick-pick action types (07 §13.9.5, affordance 4). */
export type NpcAction = 'attack' | 'move' | 'use_ability' | 'flee' | 'yield';

// ---------------------------------------------------------------------------
// Id minting
// ---------------------------------------------------------------------------

let _nextTurnLogId = 1;

/** Mint a stable turn-log entry id (monotonic within the session). */
export function mintTurnLogEntryId(): string {
  return `tlog_${_nextTurnLogId++}`;
}

/**
 * Reset the id sequence — test-only; never call in production.
 * @internal
 */
export function _resetTurnLogIdSequence(): void {
  _nextTurnLogId = 1;
}

// ---------------------------------------------------------------------------
// Pure reducer helpers
// ---------------------------------------------------------------------------

/**
 * Build a new `TurnLogEntry`. Does NOT mutate any state; callers pass the
 * minted id so this remains purely functional.
 */
export function buildTurnLogEntry(params: {
  id: string;
  round: number;
  actorEntryId: string;
  actorName: string;
  kind: TurnLogEntryKind;
  description: string;
}): TurnLogEntry {
  return {
    id: params.id,
    round: params.round,
    actorEntryId: params.actorEntryId,
    actorName: params.actorName,
    kind: params.kind,
    description: params.description,
  };
}

/**
 * Append one turn-log entry. Returns a new `TurnLog` — never mutates input.
 */
export function appendTurnLogEntry(log: TurnLog, entry: TurnLogEntry): TurnLog {
  return { entries: [...log.entries, entry] };
}

/**
 * Return all entries for a given round (1-based).
 * Returns an empty array when `round < 1` or when no entries match.
 */
export function filterTurnLogByRound(log: TurnLog, round: number): TurnLogEntry[] {
  if (round < 1) return [];
  return log.entries.filter((e) => e.round === round);
}

/**
 * Return all entries for a given actor (by `actorEntryId`).
 */
export function filterTurnLogByActor(log: TurnLog, actorEntryId: string): TurnLogEntry[] {
  return log.entries.filter((e) => e.actorEntryId === actorEntryId);
}

/** Return an empty turn log (for clearing on initiative reset). */
export function clearTurnLog(): TurnLog {
  return { entries: [] };
}

// ---------------------------------------------------------------------------
// Narration text builders (pure — no i18n, just template strings)
// ---------------------------------------------------------------------------

/**
 * Build a human-readable draft narration string for a turn-log entry.
 * These strings go into the `submit-fact` draft payload as the value of a
 * `rich_text` description fact — they are GM-authored content (not LLM),
 * so they never violate the "LLM never writes canon" invariant.
 *
 * The strings use plain English as a starting format — the caller may pass
 * them through i18n if locale-aware templates are available.
 */
export function buildNarrationText(entry: TurnLogEntry): string {
  return entry.description;
}

/**
 * Build the description text for a reaction roll result.
 *
 * @param actorName - Display name of the NPC.
 * @param rollTotal - The 2d6 total.
 * @param disposition - The disposition string stamped on the entry.
 */
export function buildReactionRollDescription(
  actorName: string,
  rollTotal: number,
  disposition: string,
): string {
  return `${actorName} reaction: ${rollTotal} — ${disposition}`;
}

/**
 * Build the description text for a damage-assignment entry.
 *
 * @param actorName - Display name of the combatant taking damage.
 * @param amount - Damage amount (positive integer).
 * @param currentHp - HP after damage (may be ≤ 0).
 * @param maxHp - Max HP if known, else undefined.
 */
export function buildDamageDescription(
  actorName: string,
  amount: number,
  currentHp: number,
  maxHp: number | undefined,
): string {
  const hpStr = maxHp !== undefined ? `${currentHp}/${maxHp}` : String(currentHp);
  const suffix = currentHp <= 0 ? ' [down]' : '';
  return `${actorName} takes ${amount} damage (now ${hpStr} HP)${suffix}`;
}

/**
 * Build the description text for a condition-applied entry.
 *
 * @param actorName - Display name of the combatant.
 * @param condition - The condition string (e.g. "prone", "grappled").
 * @param added - `true` if the condition was added; `false` if removed.
 */
export function buildConditionDescription(
  actorName: string,
  condition: string,
  added: boolean,
): string {
  return added
    ? `${actorName} gains condition: ${condition}`
    : `${actorName} loses condition: ${condition}`;
}

/**
 * Build the description text for an NPC quick-pick action.
 *
 * @param actorName - Display name of the NPC.
 * @param action - The picked action kind.
 */
export function buildNpcActionDescription(actorName: string, action: NpcAction): string {
  const labels: Record<NpcAction, string> = {
    attack: 'attacks',
    move: 'moves',
    use_ability: 'uses an ability',
    flee: 'flees',
    yield: 'yields',
  };
  return `${actorName} ${labels[action]}`;
}

/**
 * Build the description text for a save/skill check.
 *
 * @param actorName - Display name of the combatant rolling.
 * @param spec - The dice notation (e.g. "1d20").
 * @param rollTotal - The roll result.
 */
export function buildSaveDescription(
  actorName: string,
  spec: string,
  rollTotal: number,
): string {
  return `${actorName} rolls ${spec}: ${rollTotal}`;
}

/**
 * Build the description text for an end-turn-with-effect tag.
 *
 * @param actorName - Display name of the combatant.
 * @param effect - The free-text or canned effect string.
 */
export function buildEffectDescription(actorName: string, effect: string): string {
  return `${actorName}: ${effect}`;
}

// ---------------------------------------------------------------------------
// Disposition mapping for reaction rolls (2d6, default table)
// ---------------------------------------------------------------------------

/**
 * Derive a disposition label from a 2d6 reaction roll total using the
 * standard TTRPG reaction table (Moldvay/B/X default).
 *
 * 2–3   → "hostile"
 * 4–6   → "unfriendly"
 * 7–9   → "neutral"
 * 10–11 → "friendly"
 * 12    → "enthusiastic"
 */
export function reactionDisposition(total: number): string {
  if (total <= 3) return 'hostile';
  if (total <= 6) return 'unfriendly';
  if (total <= 9) return 'neutral';
  if (total <= 11) return 'friendly';
  return 'enthusiastic';
}
